// ── Live attachment thumbnail strip (C1447) ──
//
// Derives its content purely from the textarea's current value — never from an
// upload-tracking array — so it stays correct across paste, drop, the Embed ▾ picker,
// manual text edits, undo, and re-entering edit mode on an existing description, with
// zero coupling to any of those input paths. Reconciles by URL instead of rebuilding
// innerHTML on every keystroke: existing DOM nodes for refs still present are moved
// (cheap, no network refetch, no flicker), nodes for refs no longer present are removed,
// nodes for new refs are created.

// Matches `[label](url)` / `![label](url)` where url is exactly an images/files API ref —
// deliberately narrow (no lookbehind, for Safari <16.4) so this can never be pointed at an
// arbitrary/hostile URL. The placeholder `![uploading name...]()` has an empty URL and can
// never match, so a broken/loading thumbnail can never render here.
const REF_RE = /(!?)\[([^\]]*)\]\(((?:https?:\/\/[^)\s/]+)?\/api\/projects\/\d+\/(?:images|files)\/\d+)\)/g;

function buildChip(isImage, label, url, textarea) {
  const el = document.createElement('span');
  el.dataset.url = url;
  el.className = isImage ? 'embed-thumb' : 'embed-file-chip';

  if (isImage) {
    const img = document.createElement('img');
    img.src = url;
    img.alt = label || '';
    img.loading = 'lazy';
    // A deleted/404'd image must not leave a broken-image box in the strip.
    img.addEventListener('error', () => el.remove());
    el.appendChild(img);
  } else {
    const icon = document.createElement('span');
    icon.textContent = '\u{1F4CE}'; // 📎
    const name = document.createElement('span');
    name.textContent = label || url;
    name.title = label || url;
    el.appendChild(icon);
    el.appendChild(name);
  }

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'embed-thumb-remove';
  removeBtn.setAttribute('aria-label', 'Remove attachment');
  removeBtn.textContent = '×';
  // mousedown, not click — keeps focus (and caret) on the textarea, same reason the
  // embed menu itself uses mousedown (see lib/embed-menu.js).
  removeBtn.addEventListener('mousedown', (e) => e.preventDefault());
  removeBtn.addEventListener('click', () => {
    const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const refRe = new RegExp(`!?\\[[^\\]]*\\]\\(${escapedUrl}\\)\\n?`);
    textarea.value = textarea.value.replace(refRe, '');
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  });
  el.appendChild(removeBtn);

  return el;
}

// renderAttachmentStrip(stripEl, textarea) — call on every textarea `input` (and once at
// mount). Safe to call repeatedly; a no-op render touches no DOM.
export function renderAttachmentStrip(stripEl, textarea) {
  const text = textarea.value || '';
  const wanted = [];
  const seen = new Set();
  let m;
  REF_RE.lastIndex = 0;
  while ((m = REF_RE.exec(text))) {
    const [, bang, label, url] = m;
    if (seen.has(url)) continue;
    seen.add(url);
    // Not escapeHtml'd — every use below is a DOM property assignment (img.alt,
    // textContent), never innerHTML, so the raw string is already safe and correct.
    wanted.push({ isImage: bang === '!', label, url });
  }

  const existingByUrl = new Map();
  for (const el of stripEl.children) existingByUrl.set(el.dataset.url, el);

  for (const { isImage, label, url } of wanted) {
    let el = existingByUrl.get(url);
    if (el) {
      existingByUrl.delete(url);
    } else {
      el = buildChip(isImage, label, url, textarea);
    }
    // appendChild on an already-attached node moves it — cheap, doesn't recreate the
    // <img> or refetch it. This also settles final DOM order to match `wanted`.
    stripEl.appendChild(el);
  }
  for (const el of existingByUrl.values()) el.remove();
}
